import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { Menu } from '../../services/menus/menusService';
import {
  ACTIVE_DISHES_SENTINEL,
  ALL_MENUS_SENTINEL,
  reconcileCostPriceMenuSelection,
} from '../../utils/costPriceMenuFilter';
import { cn } from '../../lib/utils';

interface MenuFilterDropdownProps {
  menus: Menu[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const DROPDOWN_WIDTH = 260;

// Shared multi-select menu filter with sentinel modes (All / Active Dishes /
// one-or-more real menus). Used by both the cost price page and the recipes
// page. The dropdown body is portaled to <body> so it escapes any ancestor
// overflow:hidden clipping.
const MenuFilterDropdown: React.FC<MenuFilterDropdownProps> = ({ menus, selected, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const isAllMode = selectedSet.has(ALL_MENUS_SENTINEL);
  const isActiveDishesMode = selectedSet.has(ACTIVE_DISHES_SENTINEL);

  // Show active menus first, then inactive — easier to scan when there are many.
  // Array.prototype.sort is stable, so the relative order within each group is preserved.
  const sortedMenus = useMemo(
    () => [...menus].sort((a, b) => Number(b.isActive) - Number(a.isActive)),
    [menus]
  );

  const computeCoords = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return null;
    // Anchor dropdown to the right edge of the button, clamped to viewport.
    const left = Math.max(8, Math.min(rect.right - DROPDOWN_WIDTH, window.innerWidth - DROPDOWN_WIDTH - 8));
    return { top: rect.bottom + 4, left };
  };

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setCoords(computeCoords());
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onReposition = () => setCoords(computeCoords());
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const handleToggle = (value: string) => {
    const next = selectedSet.has(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(reconcileCostPriceMenuSelection(selected, next));
  };

  const buttonLabel = (() => {
    if (isAllMode) return t('menus.allMenus');
    if (isActiveDishesMode) return t('costPrice.menuFilter.activeDishes');
    if (selected.length === 1) {
      const m = menus.find(x => x.id === selected[0]);
      return m ? m.name : t('menus.allMenus');
    }
    return t('costPrice.menuFilter.menusCount', { count: selected.length });
  })();

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        className="flex items-center gap-2 h-[42px] px-3 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[14px] leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-neutral-800 transition-colors"
      >
        <span className="truncate max-w-[180px]">{buttonLabel}</span>
        {!isAllMode && (
          <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {selected.length}
          </span>
        )}
        <ChevronDown className="w-4 h-4" />
      </button>

      {open && coords && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: DROPDOWN_WIDTH, zIndex: 60 }}
          className="bg-white dark:bg-background-dark shadow-xl rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark p-2"
        >
          <FilterRow
            label={t('menus.allMenus')}
            selected={isAllMode}
            onClick={() => handleToggle(ALL_MENUS_SENTINEL)}
          />
          <FilterRow
            label={t('costPrice.menuFilter.activeDishes')}
            selected={isActiveDishesMode}
            onClick={() => handleToggle(ACTIVE_DISHES_SENTINEL)}
          />

          {menus.length > 0 && (
            <div className="my-1 border-t border-[#E5E7EB] dark:border-borderPrimary-dark" />
          )}

          <div className="max-h-[250px] overflow-y-auto">
            {sortedMenus.map(menu => (
              <FilterRow
                key={menu.id}
                label={menu.name}
                selected={selectedSet.has(menu.id)}
                onClick={() => handleToggle(menu.id)}
                dot={menu.isActive ? 'active' : 'inactive'}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

interface FilterRowProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  dot?: 'active' | 'inactive';
}

const FilterRow: React.FC<FilterRowProps> = ({ label, selected, onClick, dot }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left',
      'text-[14px] leading-5 text-[#344054] dark:text-textSecondary-dark',
      'hover:bg-[#F9FAFB] dark:hover:bg-neutral-800 transition-colors'
    )}
  >
    <span
      className={cn(
        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
        selected
          ? 'bg-darkGreenColor border-darkGreenColor'
          : 'bg-white dark:bg-background-dark border-[#D0D5DD] dark:border-borderPrimary-dark'
      )}
    >
      {selected && <Check className="w-3 h-3 text-white dark:text-white" strokeWidth={3} />}
    </span>
    {dot && (
      <span
        className={cn(
          'w-2 h-2 rounded-full flex-shrink-0',
          dot === 'active' ? 'bg-darkGreenColor dark:bg-darkGreenColor-dark' : 'bg-[#9CA3AF] dark:bg-[#9CA3AF]'
        )}
      />
    )}
    <span className="truncate font-medium">{label}</span>
  </button>
);

export default MenuFilterDropdown;
